import type { FieldId } from '../form/form-fields';
import type { FieldValue, FormFile } from '../form/form-fields/field-values';

/**
 * A map of callbacks which can be used to hook into the lifecycle of the FormRenderer.
 *
 * Each callback will be passed a LifecycleEvent object which will contain context from which
 * the event was triggered.
 */
export type LifecycleHandlers = {
	[FormRendererLifecycleStage.ATTACHMENT_UPLOAD_END]?: (
		event: AttachmentUploadEndLifecycleEvent,
	) => void;
	[FormRendererLifecycleStage.FIELD_CHANGED]?: (event: FieldChangedLifecycleEvent) => void;
	[FormRendererLifecycleStage.RENDER_COMPLETE]?: (event: LifecycleEvent) => void;
};

// oxlint-disable-next-line eslint/no-redeclare
export const FormRendererLifecycleStage = {
	ATTACHMENT_UPLOAD_END: 'attachmentUploadEnd',
	FIELD_CHANGED: 'fieldChanged',
	RENDER_COMPLETE: 'renderComplete',
} as const;

export type FormRendererLifecycleStage =
	(typeof FormRendererLifecycleStage)[keyof typeof FormRendererLifecycleStage];

/**
 * Note that `fieldId` will not be present if the lifecycle stage pertains to the entire form rather
 * than a specific field.
 */
export interface LifecycleEvent {
	fieldId?: FieldId;
	stage: Omit<
		FormRendererLifecycleStage,
		| typeof FormRendererLifecycleStage.ATTACHMENT_UPLOAD_END
		| typeof FormRendererLifecycleStage.FIELD_CHANGED
	>;
}

export interface AttachmentUploadEndLifecycleEvent {
	fieldId: FieldId;
	file: FormFile;
	stage: typeof FormRendererLifecycleStage.ATTACHMENT_UPLOAD_END;
}

export interface FieldChangedLifecycleEvent {
	fieldId: FieldId;
	fieldValue: FieldValue | null;
	stage: typeof FormRendererLifecycleStage.FIELD_CHANGED;
}
